import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { ApiError } from "../api/ApiError";
import { strings } from "../strings";

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

/** React error boundaries must be class components (no hook equivalent) - the one exception to
 * this codebase's function-component convention. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("evergreen-web: unhandled render error", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const correlationId = error instanceof ApiError ? error.correlationId : undefined;

    return (
      <div role="alert" className="eg-error-page">
        <h1>{strings.errors.genericTitle}</h1>
        <p>{strings.errors.genericBody}</p>
        {correlationId && (
          <p className="eg-error-page__correlation">
            {strings.errors.correlationPrefix}
            {correlationId}
          </p>
        )}
      </div>
    );
  }
}
